import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import RMM from "@/pages/RMM";
import Recurring from "@/pages/Recurring";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Assets from "@/pages/Assets";
import AssetDetail from "@/pages/AssetDetail";
import Inventory from "@/pages/Inventory";
import Network from "@/pages/Network";
import Compliance from "@/pages/Compliance";
import Security from "@/pages/Security";
import Vulnerabilities from "@/pages/Vulnerabilities";
import Docs from "@/pages/Docs";
import Vault from "@/pages/Vault";
import Contracts from "@/pages/Contracts";
import Licenses from "@/pages/Licenses";
import Backups from "@/pages/Backups";
import Reports from "@/pages/Reports";
import CustomerSuccess from "@/pages/CustomerSuccess";
import MSPBI from "@/pages/MSPBI";
import AIOps from "@/pages/AIOps";
import Agents from "@/pages/Agents";
import AgentDetail from "@/pages/AgentDetail";
import Remediation from "@/pages/Remediation";
import ClientPortal from "@/pages/ClientPortal";
import GlobalSearch from "@/pages/GlobalSearch";
import Identity from "@/pages/Identity";
import Reconciliation from "@/pages/Reconciliation";
import Evidence from "@/pages/Evidence";
import Exports from "@/pages/Exports";
import EmailEngine from "@/pages/EmailEngine";
import MFA from "@/pages/MFA";
import CustomerRisk from "@/pages/CustomerRisk";
import Leakage from "@/pages/Leakage";
import RecurringV2 from "@/pages/RecurringV2";
import ExecStorytelling from "@/pages/ExecStorytelling";
import Opportunities from "@/pages/Opportunities";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#09090B] text-zinc-400 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  // Key the boundary by path so a crash on one page is isolated and navigating
  // to another route mounts a fresh (uncrashed) subtree.
  return <Layout><ErrorBoundary key={loc.pathname}>{children}</ErrorBoundary></Layout>;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/rmm" element={<Protected><RMM /></Protected>} />
      <Route path="/recurring" element={<Protected><Recurring /></Protected>} />
      <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
      <Route path="/tickets/:id" element={<Protected><TicketDetail /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
      <Route path="/assets" element={<Protected><Assets /></Protected>} />
      <Route path="/assets/:id" element={<Protected><AssetDetail /></Protected>} />
      <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
      <Route path="/network" element={<Protected><Network /></Protected>} />
      <Route path="/compliance" element={<Protected><Compliance /></Protected>} />
      <Route path="/security" element={<Protected><Security /></Protected>} />
      <Route path="/vulnerabilities" element={<Protected><Vulnerabilities /></Protected>} />
      <Route path="/docs" element={<Protected><Docs /></Protected>} />
      <Route path="/vault" element={<Protected><Vault /></Protected>} />
      <Route path="/contracts" element={<Protected><Contracts /></Protected>} />
      <Route path="/licenses" element={<Protected><Licenses /></Protected>} />
      <Route path="/backups" element={<Protected><Backups /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/customer-success" element={<Protected><CustomerSuccess /></Protected>} />
      <Route path="/msp-bi" element={<Protected><MSPBI /></Protected>} />
      <Route path="/ai" element={<Protected><AIOps /></Protected>} />
      <Route path="/agents" element={<Protected><Agents /></Protected>} />
      <Route path="/agents/:id" element={<Protected><AgentDetail /></Protected>} />
      <Route path="/remediation" element={<Protected><Remediation /></Protected>} />
      <Route path="/portal" element={<Protected><ClientPortal /></Protected>} />
      <Route path="/search" element={<Protected><GlobalSearch /></Protected>} />
      <Route path="/identity" element={<Protected><Identity /></Protected>} />
      <Route path="/reconciliation" element={<Protected><Reconciliation /></Protected>} />
      <Route path="/evidence" element={<Protected><Evidence /></Protected>} />
      <Route path="/exports" element={<Protected><Exports /></Protected>} />
      <Route path="/email" element={<Protected><EmailEngine /></Protected>} />
      <Route path="/mfa" element={<Protected><MFA /></Protected>} />
      <Route path="/customer-risk" element={<Protected><CustomerRisk /></Protected>} />
      <Route path="/leakage" element={<Protected><Leakage /></Protected>} />
      <Route path="/recurring-v2" element={<Protected><RecurringV2 /></Protected>} />
      <Route path="/storytelling" element={<Protected><ExecStorytelling /></Protected>} />
      <Route path="/opportunities" element={<Protected><Opportunities /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#0a0a0c", border: "1px solid #27272A", color: "#fafafa" } }} />
      </AuthProvider>
    </BrowserRouter>
  );
}
